import { useEffect, useState } from "react";
import { BellRing, BellOff, Loader2, X, Share } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { pushStatus, enablePush, disablePush } from "../lib/push";

const usePushState = () => {
  const [st, setSt] = useState("loading");
  const refresh = () => pushStatus().then(setSt).catch(() => setSt("unsupported"));
  useEffect(() => { refresh(); }, []);
  return [st, refresh];
};

export const PushToggle = () => {
  const [st, refresh] = usePushState();
  const [busy, setBusy] = useState(false);
  if (st === "loading" || st === "unsupported") return null;
  const flip = async () => {
    setBusy(true);
    try {
      if (st === "on") { await disablePush(); toast.success("Phone notifications off"); }
      else { await enablePush(); toast.success("Phone notifications on"); }
    } catch (e) { toast.error(e.message); }
    await refresh();
    setBusy(false);
  };
  if (st === "needs-install") {
    return <span data-testid="push-needs-install" className="flex items-center gap-2 border border-white/10 text-[#9CA3AF] text-xs px-3 py-2.5 rounded-lg"><Share size={13} /> Add to Home Screen for alerts</span>;
  }
  return (
    <button data-testid="push-toggle" onClick={flip} disabled={busy || st === "denied"} title={st === "denied" ? "Blocked in browser settings" : ""}
      className={`flex items-center gap-2 border font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50 ${st === "on" ? "border-[#31A24C]/50 text-[#31A24C]" : "border-white/10 text-[#9CA3AF] hover:text-white"}`}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : st === "on" ? <BellRing size={14} /> : <BellOff size={14} />}
      {st === "denied" ? "Alerts blocked" : st === "on" ? "Alerts: on" : "Alerts: off"}
    </button>
  );
};

// One-time prompt for signed-in members; permission is only requested on their tap.
export const PushPrompt = () => {
  const { user } = useAuth();
  const [st, refresh] = usePushState();
  const [hidden, setHidden] = useState(() => localStorage.getItem("kv-push-prompted") === "1");
  const [busy, setBusy] = useState(false);
  if (!user || hidden || !["off", "needs-install"].includes(st)) return null;
  const close = () => { localStorage.setItem("kv-push-prompted", "1"); setHidden(true); };
  const on = async () => {
    setBusy(true);
    try { await enablePush(); toast.success("You'll get alerts on this device"); close(); }
    catch (e) { toast.error(e.message); }
    await refresh();
    setBusy(false);
  };
  return (
    <div data-testid="push-prompt" className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md rounded-2xl border border-[rgb(var(--kv-glow)/0.4)] bg-[#0F1014]/95 backdrop-blur-xl p-4 shadow-[0_0_30px_-6px_rgb(var(--kv-glow)/0.5)] fade-up">
      <button data-testid="push-prompt-close" onClick={close} className="absolute top-3 right-3 text-[#71717A] hover:text-white"><X size={16} /></button>
      <div className="flex gap-3 items-start pr-6">
        <BellRing className="text-[rgb(var(--kv-glow))] shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-white font-semibold text-sm">Get alerts on this device</p>
          <p className="text-[#A1A1AA] text-xs mt-1">
            {st === "needs-install" ? "On iPhone: tap Share, then “Add to Home Screen”, open KinkVault from there and turn alerts on."
              : "Messages, new followers, likes, comments, mentions and new posts from people you follow."}
          </p>
          {st === "off" && (
            <div className="flex gap-2 mt-3">
              <button data-testid="push-prompt-enable" onClick={on} disabled={busy} className="flex items-center gap-1.5 bg-[rgb(var(--kv-glow))] text-black font-semibold text-xs px-4 py-2 rounded-full disabled:opacity-50">
                {busy && <Loader2 size={12} className="animate-spin" />} Turn on
              </button>
              <button onClick={close} className="text-[#A1A1AA] text-xs px-3 py-2 hover:text-white">Not now</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
